'use client';

import React from 'react';
import { motion } from 'framer-motion';
import {
    Package,
    CreditCard,
    Lock,
    ShoppingCart,
    TrendingDown,
    Bell as BellIcon,
    Check,
    Trash2
} from 'lucide-react';
import { NotificationData, NotificationType } from '@/providers/NotificationCenterProvider';
import { useRouter } from 'next/navigation';
import { useBasePath } from '@/providers/BasePathProvider';

interface NotificationCardProps {
    notification: NotificationData;
    onMarkAsRead: (id: string) => void;
    onDelete: (id: string) => void;
    onClose?: () => void;
}

/* ---------------------------------- */
/* Helpers                            */
/* ---------------------------------- */

const getNotificationIcon = (type: NotificationType) => {
    const className = 'w-5 h-5';

    switch (type) {
        case 'ORDER_CONFIRMATION':
        case 'ORDER_SHIPPED':
        case 'ORDER_DELIVERED':
            return <Package className={className} />;
        case 'PAYMENT_FAILED':
        case 'REFUND_PROCESSED':
            return <CreditCard className={className} />;
        case 'PASSWORD_RESET':
        case 'NEW_DEVICE_LOGIN':
        case 'TWO_FA_CODE':
            return <Lock className={className} />;
        case 'ABANDONED_CART_REMINDER':
            return <ShoppingCart className={className} />;
        case 'PRICE_DROP_ALERT':
            return <TrendingDown className={className} />;
        case 'BACK_IN_STOCK':
        default:
            return <BellIcon className={className} />;
    }
};

const getNotificationColor = (type: NotificationType) => {
    switch (type) {
        case 'ORDER_CONFIRMATION':
        case 'ORDER_DELIVERED':
        case 'REFUND_PROCESSED':
            return 'bg-green-100 text-green-600';
        case 'ORDER_SHIPPED':
            return 'bg-blue-100 text-blue-600';
        case 'PAYMENT_FAILED':
            return 'bg-red-100 text-red-600';
        case 'PASSWORD_RESET':
        case 'NEW_DEVICE_LOGIN':
        case 'TWO_FA_CODE':
            return 'bg-purple-100 text-purple-600';
        case 'ABANDONED_CART_REMINDER':
        case 'PRICE_DROP_ALERT':
        case 'BACK_IN_STOCK':
            return 'bg-orange-100 text-orange-600';
        default:
            return 'bg-slate-100 text-slate-700';
    }
};

const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const diff = Date.now() - date.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} min ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString();
};

/* ---------------------------------- */
/* Component                          */
/* ---------------------------------- */

export function NotificationCard({
    notification,
    onMarkAsRead,
    onDelete,
    onClose
}: NotificationCardProps) {
    const router = useRouter();
    const { buildPath } = useBasePath();

    const handleClick = () => {
        // Mark as read if needed
        if (!notification.isRead) {
            onMarkAsRead(notification._id);
        }

        // Close the dropdown immediately to provide instant feedback
        if (onClose) {
            onClose();
        }

        // Navigate if there's a link
        if (notification.data?.orderId) {
            router.push(buildPath(`/account/orders/${notification.data.orderId}`));
        } else if (notification.data?.productId) {
            router.push(buildPath(`/products/${notification.data.productId}`));
        }
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete(notification._id);
    };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
            className={`group relative flex gap-3 py-6 px-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer ${notification.isRead ? '' : 'bg-gray-100/40'}`}
            onClick={handleClick}
        >
            {/* Unread Indicator Dot */}
            {/* {!notification.isRead && (
                <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-900 mt-2.5" />
            )}
            {notification.isRead && (
                <div className="flex-shrink-0 w-2 h-2" />
            )} */}

            {/* Icon Circle */}
            <div
                className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${getNotificationColor(notification.type)}`}
            >
                {getNotificationIcon(notification.type)}
            </div>

            {/* Right Section: Content + Timestamp */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Top Row: Title + Timestamp */}
                <div className="flex items-start justify-between gap-2">
                    <h4 className="text-sm font-semibold text-gray-800 leading-tight">
                        {notification.title}
                    </h4>
                    <span className="text-xs text-gray-500 font-medium whitespace-nowrap flex-shrink-0">
                        {formatRelativeTime(notification.createdAt)}
                    </span>
                </div>
                {/* Bottom Row: Description */}
                <p className="text-sm text-gray-500 line-clamp-2 leading-snug mt-0.5">
                    {notification.body}
                </p>
            </div>

            {/* Action Buttons - Overlaid on hover */}
            <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {!notification.isRead && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onMarkAsRead(notification._id);
                        }}
                        className="p-2 rounded-lg hover:bg-gray-200 transition text-gray-400 hover:text-gray-700 bg-white"
                        aria-label="Mark as read"
                    >
                        <Check className="w-4 h-4" />
                    </button>
                )}
                <button
                    onClick={handleDelete}
                    className="p-2 rounded-lg hover:bg-red-100 transition text-gray-400 hover:text-red-600 bg-white"
                    aria-label="Delete notification"
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>
        </motion.div>
    );
}
